import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  Typography,
  Box,
  IconButton,
  ListItemText,
  Checkbox,
  TextField,
  SelectChangeEvent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tooltip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useLocation, useNavigate } from "react-router-dom";
import { AppDispatch, RootState } from "../redux/store";
import { deleteUser, updateUser, viewUser } from "../redux/slices/authSlice";
import { UserUpdateData } from "../interfaces/user-interface";
import { getUserRole, getUserRoleNames } from "../redux/slices/userRoleSlice";
import {
  PermissionsByDocKey,
  PermissionsItem,
} from "../interfaces/permissions-interface";
import { getPermissions } from "../redux/slices/permissionsSlice";
import { UserRole } from "../interfaces/user-role.interface";
import useDetectSession from "../hooks/useDetectSession.hook";
import {
  inputStyle,
  pageHeading,
  primaryBtnStyle,
  primaryBtnStyleOutline,
} from "../styles/styling";
import AppContainer from "../components/AppContainer";
import { transformArrToObj } from "../utils/transform-permissions-obj";

const ViewUserPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();

  const navigate = useNavigate();
  const location = useLocation();
  const { userId: id } = location.state;
  const [formData, setFormData] = useState<UserUpdateData>({
    email: "",
    password: "",
    role: "",
    fullName: "",
    createdBy: "",
    createdAt: { _seconds: 0, _nanoseconds: 0 },
  });
  const [roleList, setRoleList] = useState<string[]>([]);
  const [edit, setEdit] = useState<boolean>(false);
  const [passwordPlaceholder, setPasswordPlaceholder] =
    useState<string>("password");

  const selectedUser = useSelector(
    (state: RootState) => state.auth.selectedUser
  );
  const { permissions } = useSelector((state: RootState) => state.permissions);
  const currentUser = useSelector((state: RootState) => state.auth.currentUser);

  const [currentUserPermissions, setCurrentUserPermissions] =
    useState<PermissionsByDocKey>();

  const [selectedPermissions, setSelectedPermissions] = useState<{
    [key: string]: PermissionsItem;
  }>({});
  const [updateUserPermissionsPayload, setUpdateUserPermissionsPayload] =
    useState<
      {
        moduleName: string;
        permissionKey: string;
        permissions: {
          view: boolean;
          edit: boolean;
          delete: boolean;
        };
      }[]
    >([]);

  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [deletePromptTitle, setDeletePromptTitle] = useState<string>("");
  const [deletePromptMessage, setDeletePromptMessage] = useState<string>("");

  useDetectSession();

  useEffect(() => {
    if (currentUser && currentUser.userPermissions) {
      setCurrentUserPermissions(transformArrToObj(currentUser.userPermissions));
    }
  }, [currentUser]);

  useEffect(() => {
    dispatch(getPermissions());
    const fetchRoles = async () => {
      const list = await dispatch(getUserRoleNames()).unwrap();

      if (list) {
        setRoleList(list);
      }
    };

    fetchRoles();
  }, [dispatch]);

  useEffect(() => {
    if (selectedUser && selectedUser.data) {
      setFormData({
        email: selectedUser.data.email,
        role: selectedUser.data.role,
        fullName: selectedUser.data.fullName,
        createdBy: selectedUser.data.createdBy,
        createdAt: selectedUser.data.createdAt,
      });
      const permissionsByKey: any = {};
      selectedUser.data.userPermissions?.forEach(
        (rolePermissionItem: PermissionsItem) =>
          (permissionsByKey[rolePermissionItem.permissionKey] = {
            ...rolePermissionItem,
          })
      );
      setSelectedPermissions(permissionsByKey);
      setUpdateUserPermissionsPayload(selectedUser.data.userPermissions ?? []);
    }
  }, [selectedUser]);

  if (!selectedUser) {
    return null;
  }

  const handleBackClick = () => {
    navigate(-1);
  };

  const handleTextInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });

    dispatch(getUserRole({ data: { roleName: e.target.value } }))
      .unwrap()
      .then((item: UserRole) => {
        const permissionsByKey: any = {};
        item.rolePermissions?.forEach(
          (rolePermissionItem: PermissionsItem) =>
            (permissionsByKey[rolePermissionItem.permissionKey] = {
              ...rolePermissionItem,
            })
        );
        setSelectedPermissions(permissionsByKey);
        setUpdateUserPermissionsPayload(item.rolePermissions);
      });
  };

  const handleCancelClick = () => {
    setEdit(false);
  };

  const handleSaveClick = async () => {
    await dispatch(
      updateUser({
        id,
        data: {
          ...formData,
          userPermissions: updateUserPermissionsPayload,
          updatedBy: currentUser?.fullname ?? "",
        },
      })
    )
      .unwrap()
      .then(() => {
        setEdit(false);
        navigate("/dashboard");
      });
  };

  const buildUpdateUserPermissionsPayload = (
    permissionItem: PermissionsItem
  ) => {
    const isPreviouslyIncluded = updateUserPermissionsPayload?.find(
      (item: PermissionsItem) =>
        item.permissionKey === permissionItem.permissionKey
    );
    if (!isPreviouslyIncluded) {
      setUpdateUserPermissionsPayload([
        ...(updateUserPermissionsPayload ?? []),
        permissionItem,
      ]);
    } else {
      setUpdateUserPermissionsPayload(
        updateUserPermissionsPayload?.map((item: PermissionsItem) => {
          if (item.permissionKey === permissionItem.permissionKey) {
            return permissionItem;
          }
          return item;
        })
      );
    }
  };

  const handleDeleteIconClick = (id: string, active: boolean) => {
    if (active) {
      setDeletePromptTitle("Deactivate User");
      setDeletePromptMessage("Are you sure you want to deactivate this user?");
    } else {
      setDeletePromptTitle("Activate User");
      setDeletePromptMessage("Are you sure you want to activate this user?");
    }
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (id) {
      dispatch(deleteUser({ id, deletedBy: currentUser?.fullname ?? "" }))
        .unwrap()
        .then(() => {
          setDeleteModalOpen(false);
          dispatch(viewUser({ id })).unwrap();
        });
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
  };

  return (
    <AppContainer>
      <Box display="flex" alignItems="center" marginBottom={2}>
        <IconButton onClick={handleBackClick}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" sx={pageHeading}>
          Edit User
        </Typography>
        <Box
          style={{
            position: "absolute",
            right: 15,
            flexDirection: "row",
            display: "flex",
          }}
        >
          <Tooltip
            title={`${
              selectedUser?.data?.active ? "Deactivate" : "Activate"
            } User`}
          >
            <Switch
              disabled={
                !currentUserPermissions?.productsList?.permissions?.edit
              }
              defaultChecked={selectedUser?.data?.active}
              checked={selectedUser?.data?.active}
              onChange={(_e) =>
                handleDeleteIconClick(
                  selectedUser?.data?.id,
                  selectedUser?.data?.active
                )
              }
            />
          </Tooltip>
          {currentUserPermissions?.user?.permissions?.edit && (
            <Button
              variant="contained"
              onClick={() => {
                setEdit(true);
              }}
              disabled={edit}
              sx={[primaryBtnStyle, { marginLeft: "5px" }]}
            >
              Edit
            </Button>
          )}
        </Box>
      </Box>

      <TextField
        label="Full Name"
        name="fullName"
        value={formData?.fullName}
        onChange={handleTextInputChange}
        fullWidth
        margin="normal"
        disabled
        sx={inputStyle()}
      />
      <TextField
        label="Email"
        name="email"
        value={formData?.email}
        onChange={handleTextInputChange}
        fullWidth
        margin="normal"
        disabled
        sx={inputStyle()}
      />
      <TextField
        label="Password"
        type="password"
        name={"password"}
        placeholder="********"
        defaultValue={passwordPlaceholder}
        value={
          !formData.password || formData?.password === ""
            ? passwordPlaceholder
            : formData.password
        }
        onChange={handleTextInputChange}
        fullWidth
        margin="normal"
        disabled={!edit}
        onFocus={() => {
          setPasswordPlaceholder("");
        }}
        onBlur={() => {
          console.log("test", formData.password);
          if (!formData.password || formData.password === "") {
            setPasswordPlaceholder("password");
          } else {
            setPasswordPlaceholder("");
          }
        }}
        sx={inputStyle()}
      />
      <FormControl fullWidth margin="normal">
        <InputLabel>Role</InputLabel>
        <Select
          label="role"
          name="role"
          value={formData?.role ?? ""}
          onChange={handleSelectChange}
          renderValue={(selected) => selected}
          disabled={!edit}
        >
          {roleList.map((item) => (
            <MenuItem key={item} value={item}>
              <ListItemText primary={item} />
            </MenuItem>
          ))}
        </Select>
        <TextField
          label="Created By"
          name="createdBy"
          value={formData?.createdBy}
          fullWidth
          margin="normal"
          disabled
          sx={inputStyle()}
        />
        <TextField
          label="Date Created"
          name="dateCreated"
          value={new Date(formData?.createdAt._seconds * 1000).toLocaleString()}
          fullWidth
          margin="normal"
          disabled
          sx={inputStyle()}
        />
      </FormControl>

      {/* Permissions Options */}
      <Box
        display="flex"
        alignItems="center"
        margin={2}
        flexDirection={"column"}
      >
        <Typography variant="h6" textAlign={"center"} margin={2}>
          User Permissions
        </Typography>
        <TableContainer
          component={Paper}
          sx={{
            overflowX: "initial",
            "& th": {
              backgroundColor: "#E1E1E1",
            },
          }}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Module Name</TableCell>
                <TableCell>View</TableCell>
                <TableCell>Edit</TableCell>
                <TableCell>Delete</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {permissions?.map((item: PermissionsItem) => (
                <TableRow key={item?.moduleName}>
                  <TableCell>{item?.moduleName}</TableCell>
                  <TableCell>
                    {item?.permissions?.view && (
                      <Checkbox
                        checked={
                          selectedPermissions[item.permissionKey]?.permissions
                            ?.view ?? false
                        }
                        onClick={(_e) => {
                          const modifiedItem = {
                            permissionKey: item.permissionKey,
                            moduleName: item.moduleName,
                            permissions: {
                              view: !selectedPermissions[item.permissionKey]
                                ?.permissions?.view,
                              edit: selectedPermissions[item.permissionKey]
                                ?.permissions?.edit,
                              delete:
                                selectedPermissions[item.permissionKey]
                                  ?.permissions?.delete,
                            },
                          };
                          setSelectedPermissions({
                            ...selectedPermissions,
                            [item.permissionKey]: modifiedItem,
                          });
                          buildUpdateUserPermissionsPayload(modifiedItem);
                        }}
                        disabled={!edit}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {item?.permissions?.edit && (
                      <Checkbox
                        checked={
                          selectedPermissions[item.permissionKey]?.permissions
                            ?.edit ?? false
                        }
                        onClick={(_e) => {
                          const modifiedItem = {
                            permissionKey: item.permissionKey,
                            moduleName: item.moduleName,
                            permissions: {
                              view: !selectedPermissions[item.permissionKey]
                                ?.permissions?.edit
                                ? true
                                : selectedPermissions[item.permissionKey]
                                    ?.permissions?.view,
                              edit: !selectedPermissions[item.permissionKey]
                                ?.permissions?.edit,
                              delete:
                                selectedPermissions[item.permissionKey]
                                  ?.permissions?.delete,
                            },
                          };
                          setSelectedPermissions({
                            ...selectedPermissions,
                            [item.permissionKey]: modifiedItem,
                          });
                          buildUpdateUserPermissionsPayload(modifiedItem);
                        }}
                        disabled={!edit}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {item?.permissions?.delete && (
                      <Checkbox
                        checked={
                          selectedPermissions[item.permissionKey]?.permissions
                            ?.delete ?? false
                        }
                        onClick={(_e) => {
                          const modifiedItem = {
                            permissionKey: item.permissionKey,
                            moduleName: item.moduleName,
                            permissions: {
                              view: !selectedPermissions[item.permissionKey]
                                ?.permissions?.delete
                                ? true
                                : selectedPermissions[item.permissionKey]
                                    ?.permissions?.view,
                              edit: selectedPermissions[item.permissionKey]
                                ?.permissions?.edit,
                              delete:
                                !selectedPermissions[item.permissionKey]
                                  ?.permissions?.delete,
                            },
                          };
                          setSelectedPermissions({
                            ...selectedPermissions,
                            [item.permissionKey]: modifiedItem,
                          });
                          buildUpdateUserPermissionsPayload(modifiedItem);
                        }}
                        disabled={!edit}
                      />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      {edit && (
        <Box
          display="flex"
          margin={2}
          flexDirection={"row"}
          alignItems={"flex-end"}
          justifyContent={"flex-end"}
        >
          <Button
            variant="contained"
            onClick={handleSaveClick}
            sx={primaryBtnStyle}
          >
            Save
          </Button>
          <Button
            variant="outlined"
            style={{ marginLeft: 15 }}
            onClick={handleCancelClick}
            sx={primaryBtnStyleOutline}
          >
            Cancel
          </Button>
        </Box>
      )}

      <Dialog
        open={deleteModalOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="Deactivate"
      >
        <DialogTitle id="alert-dialog-title">{deletePromptTitle}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {deletePromptMessage}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleDeleteCancel}
            variant="outlined"
            sx={primaryBtnStyleOutline}
          >
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} autoFocus sx={primaryBtnStyle}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </AppContainer>
  );
};

export default ViewUserPage;
